import { Action, createReducer, on, combineReducers, createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromBusinessDetailReducer from './reducers/business-detail-update.reducer';
import * as fromBusinessFicaFile from './reducers/business-fica-file.reducer';
import * as fromAddDirector from './reducers/add-director.reducer';
import * as fromGetDirectors from './reducers/get-directors.reducer';
import * as fromBusinessAdministrators from './reducers/business-administrators.reducer';
import * as fromDownloadDocument from './reducers/download-document.reducer';
import * as fromUploadDocuments from './reducers/upload-documents.reducer';
import * as fromAddAdmin from './reducers/add-admin.reducer';
import * as fromEditUserRole from './reducers/edit-user-role.reducer';
import * as fromDeactivateAccount from './reducers/deactivate-account.reducer';
import * as fromTrustedDomain from './reducers/trusted-domain.reducer';
import * as fromGetBusinessDetails from './reducers/get-business-details.reducer';
import * as fromProofBusinessFicaDocs from './reducers/proof-business-fica-docs.reducer';
import * as fromDirectorsPendingBusinessManagementUserQuickSearch from './reducers/directors-pending-business-management-user-quick-search.reducer';
import * as fromGetPreviousDirectorsInvite from './reducers/previous-directors-and-admins.reducer';
import * as fromSearchExitingUserLegalEntitySetup from './reducers/search-exiting-user-legal-entity-setup.reducer';
import * as fromGetPreviousAdmins from './reducers/previous-admins.reducer';
import * as fromAdministratorsPendingBusinessManagementUserQuickSearch from './reducers/administrators-pending-business-management-user-quick-search.reducer';

export const businessDetailFeatureKey = 'businessDetail';

export interface BusinessDetailMainState {
  [fromBusinessDetailReducer.businessDetailUpdateFeatureKey]: fromBusinessDetailReducer.BusinessDetailUpdateState;
  [fromBusinessFicaFile.businessFicaFileFeatureKey]: fromBusinessFicaFile.BusinessFicaFileState;
  [fromAddDirector.addDirectorFeatureKey]: fromAddDirector.AddDirectorState;
  [fromGetDirectors.getDirectorsFeatureKey]: fromGetDirectors.GetDirectorsState;
  [fromBusinessAdministrators.businessAdministratorsFeatureKey]: fromBusinessAdministrators.BusinessAdministratorsState;
  [fromDownloadDocument.downloadDocumentFeatureKey]: fromDownloadDocument.DownloadDocumentState;
  [fromUploadDocuments.uploadDocumentsFeatureKey]: fromUploadDocuments.UploadDocumentsState;
  [fromAddAdmin.addAdminFeatureKey]: fromAddAdmin.AddAdminState;
  [fromEditUserRole.editUserRoleFeatureKey]: fromEditUserRole.EditUserRoleState;
  [fromDeactivateAccount.deactivateAccountFeatureKey]: fromDeactivateAccount.DeactivateAccountState;
  [fromTrustedDomain.trustedDomainFeatureKey]: fromTrustedDomain.TrustedDomainState;
  [fromGetBusinessDetails.getBusinessDetailsFeatureKey]: fromGetBusinessDetails.GetBusinessDetailsState;
  [fromProofBusinessFicaDocs.proofBusinessFicaDocsFeatureKey]: fromProofBusinessFicaDocs.ProofBusinessFicaDocsState;
  [fromDirectorsPendingBusinessManagementUserQuickSearch.directorsPendingInvitedBusinessManagementUserQuickSearchFeatureKey]: fromDirectorsPendingBusinessManagementUserQuickSearch.DirectorsPendingInvitedBusinessManagementUserQuickSearchState;
  [fromGetPreviousDirectorsInvite.previousDirectorsAndAdminsFeatureKey]: fromGetPreviousDirectorsInvite.State;
  [fromSearchExitingUserLegalEntitySetup.searchExitingUserLegalEntitySetupFeatureKey]: fromSearchExitingUserLegalEntitySetup.SearchExitingUserLegalEntitySetupState;
  [fromGetPreviousAdmins.previousAdminsFeatureKey]: fromGetPreviousAdmins.State;
  [fromAdministratorsPendingBusinessManagementUserQuickSearch.administratorsPendingBusinessManagementUserQuickSearchFeatureKey]: fromAdministratorsPendingBusinessManagementUserQuickSearch.AdministratorsPendingBusinessManagementUserQuickSearchState;
}

export interface State {
  [businessDetailFeatureKey]: BusinessDetailMainState;
}

export function reducers(state: BusinessDetailMainState | undefined, action: Action) {
  return combineReducers({
    [fromBusinessDetailReducer.businessDetailUpdateFeatureKey]: fromBusinessDetailReducer.reducer,
    [fromBusinessFicaFile.businessFicaFileFeatureKey]: fromBusinessFicaFile.reducer,
    [fromAddDirector.addDirectorFeatureKey]: fromAddDirector.reducer,
    [fromGetDirectors.getDirectorsFeatureKey]: fromGetDirectors.reducer,
    [fromBusinessAdministrators.businessAdministratorsFeatureKey]: fromBusinessAdministrators.reducer,
    [fromDownloadDocument.downloadDocumentFeatureKey]: fromDownloadDocument.reducer,
    [fromUploadDocuments.uploadDocumentsFeatureKey]: fromUploadDocuments.reducer,
    [fromAddAdmin.addAdminFeatureKey]: fromAddAdmin.reducer,
    [fromEditUserRole.editUserRoleFeatureKey]: fromEditUserRole.reducer,
    [fromDeactivateAccount.deactivateAccountFeatureKey]: fromDeactivateAccount.reducer,
    [fromTrustedDomain.trustedDomainFeatureKey]: fromTrustedDomain.reducer,
    [fromGetBusinessDetails.getBusinessDetailsFeatureKey]: fromGetBusinessDetails.reducer,
    [fromProofBusinessFicaDocs.proofBusinessFicaDocsFeatureKey]: fromProofBusinessFicaDocs.reducer,
    [fromDirectorsPendingBusinessManagementUserQuickSearch.directorsPendingInvitedBusinessManagementUserQuickSearchFeatureKey]:
      fromDirectorsPendingBusinessManagementUserQuickSearch.reducer,
    [fromGetPreviousDirectorsInvite.previousDirectorsAndAdminsFeatureKey]: fromGetPreviousDirectorsInvite.reducer,
    [fromSearchExitingUserLegalEntitySetup.searchExitingUserLegalEntitySetupFeatureKey]: fromSearchExitingUserLegalEntitySetup.reducer,
    [fromGetPreviousAdmins.previousAdminsFeatureKey]: fromGetPreviousAdmins.reducer,
    [fromAdministratorsPendingBusinessManagementUserQuickSearch.administratorsPendingBusinessManagementUserQuickSearchFeatureKey]:
      fromAdministratorsPendingBusinessManagementUserQuickSearch.reducer
  })(state, action);
}

export const selectBusinessDetailMainState = createFeatureSelector<State, BusinessDetailMainState>(businessDetailFeatureKey);

export const selectProofBusinessFicaDocsState = createSelector(
  selectBusinessDetailMainState,
  state => state[fromProofBusinessFicaDocs.proofBusinessFicaDocsFeatureKey]
);

export const selectSearchExitingUserLegalEntitySetupState = createSelector(
  selectBusinessDetailMainState,
  state => state[fromSearchExitingUserLegalEntitySetup.searchExitingUserLegalEntitySetupFeatureKey]
);

export const selectDirectorsPendingBusinessManagementUsersState = createSelector(
  selectBusinessDetailMainState,
  state => state[fromDirectorsPendingBusinessManagementUserQuickSearch.directorsPendingInvitedBusinessManagementUserQuickSearchFeatureKey]
);

export const selectAdministratorsPendingBusinessManagementUsersState = createSelector(
  selectBusinessDetailMainState,
  state =>
    state[fromAdministratorsPendingBusinessManagementUserQuickSearch.administratorsPendingBusinessManagementUserQuickSearchFeatureKey]
);
